import { useState, useEffect, useCallback, useRef } from 'react';
import { consolidatedBusinessApi } from '../services/consolidatedBusinessApi';
import { reconcileFilters } from '../types/consolidatedBusiness';
import type {
    ConsBusinessRow, ConsBusinessStats, ConsBusinessFilterOptions, ConsBusinessFilters,
    ConsBusinessSortField, SortOrder, KpiMode,
} from '../types/consolidatedBusiness';
import {
    DEFAULT_FILTERS, DEFAULT_KPI_MODE, DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER,
    hasListState, readListState,
} from '../components/consolidated-business/urlState';

interface UseConsBusinessReturn {
    items: ConsBusinessRow[];
    total: number;
    loading: boolean;
    error: string | null;
    stats: ConsBusinessStats | null;
    filterOptions: ConsBusinessFilterOptions | null;
    filters: ConsBusinessFilters;
    kpiMode: KpiMode;
    sortBy: ConsBusinessSortField;
    sortOrder: SortOrder;
    page: number;
    limit: number;
    setFilters: (f: ConsBusinessFilters) => void;
    setKpiMode: (m: KpiMode) => void;
    setSort: (field: ConsBusinessSortField) => void;
    setPage: (p: number) => void;
    refresh: () => void;
}

const SESSION_KEY = 'consbusiness-state';
// A bare (unfiltered) list must return the full grain, so the default filter state applies NO
// window / min-sales / status. The business-unit scope is NOT a filter: it is applied
// server-side on every query (D4) and cannot be switched off from here.
// Shared with urlState.ts, which the query string (S3-04) is graded against the same way.
const DEFAULT_KPI = DEFAULT_KPI_MODE;
const DEFAULT_SORT = DEFAULT_SORT_FIELD;
const DEFAULT_ORDER = DEFAULT_SORT_ORDER;

interface Persisted {
    filters: ConsBusinessFilters;
    kpiMode: KpiMode;
    sortBy: ConsBusinessSortField;
    sortOrder: SortOrder;
    page: number;
}

/**
 * Migrate a filter object written by an older bundle.
 *
 * `onlyUnchecked: true` became `status: 'Open'` (migration 132). The filters live in
 * sessionStorage and are written back on every change, so without this the dead field would be
 * carried forward forever — and the user would see the "Open" pill inactive while the list is
 * in fact filtered to open combinations, because the API still honours the alias for one deploy.
 *
 * `memberCount` sorting went with the batch grain; anything unknown falls back to the default.
 */
function sanitizeFilters(raw: unknown): ConsBusinessFilters {
    if (!raw || typeof raw !== 'object') return DEFAULT_FILTERS;
    const f = { ...(raw as ConsBusinessFilters & { onlyUnchecked?: boolean; supplierDimId?: string; itemCategoryId?: string }) };
    if (f.onlyUnchecked && !f.status) f.status = 'Open';
    delete f.onlyUnchecked;
    // Id filters from before the pickers switched to names. An id cannot be mapped back to its
    // name here without the options, and kept as-is it would filter invisibly: no picker shows it.
    delete f.supplierDimId;
    delete f.itemCategoryId;
    if (f.status && !VALID_STATUS.includes(f.status)) delete f.status;
    // Not a cast: sessionStorage can hold anything, and `hideLost: "false"` is truthy.
    f.hideLost = f.hideLost === true ? true : undefined;
    return reconcileFilters(f);
}

const VALID_STATUS: string[] = ['Open', 'InProgress', 'Checked', 'Lost'];

function sanitizeSort(raw: unknown): ConsBusinessSortField {
    return VALID_SORT.includes(raw as string) ? raw as ConsBusinessSortField : DEFAULT_SORT;
}

const VALID_SORT: string[] = [
    'customerName', 'productName', 'salesManagerName', 'lastInvoice',
    'kpiYTD', 'kpi12m', 'kpiYearAvg', 'avgCycle', 'daysSince',
];

/**
 * A deep link wins over whatever is remembered in sessionStorage (design audit S3-04): a
 * filtered, sorted, paged view shared or reloaded must reproduce exactly that view, not a
 * blend with the previous visit. Plain navigation to the page (no recognised query param) falls
 * back to the session's last state, as before.
 */
function loadPersisted(): Persisted {
    const fallback: Persisted = { filters: DEFAULT_FILTERS, kpiMode: DEFAULT_KPI, sortBy: DEFAULT_SORT, sortOrder: DEFAULT_ORDER, page: 1 };
    if (typeof window === 'undefined') return fallback;
    const params = new URLSearchParams(window.location.search);
    if (hasListState(params)) {
        const fromUrl = readListState(params, fallback);
        return { ...fromUrl, filters: reconcileFilters(fromUrl.filters) };
    }
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY);
        if (!raw) return fallback;
        const p = JSON.parse(raw) as Partial<Persisted>;
        return {
            filters: sanitizeFilters(p.filters),
            kpiMode: p.kpiMode ?? DEFAULT_KPI,
            sortBy: sanitizeSort(p.sortBy),
            sortOrder: p.sortOrder ?? DEFAULT_ORDER,
            page: 1,
        };
    } catch {
        return fallback;
    }
}

export function useConsolidatedBusiness(): UseConsBusinessReturn {
    const [items, setItems] = useState<ConsBusinessRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<ConsBusinessStats | null>(null);
    const [filterOptions, setFilterOptions] = useState<ConsBusinessFilterOptions | null>(null);

    const initial = loadPersisted();
    const [filters, setFiltersState] = useState<ConsBusinessFilters>(initial.filters);
    const [kpiMode, setKpiModeState] = useState<KpiMode>(initial.kpiMode);
    const [sortBy, setSortBy] = useState<ConsBusinessSortField>(initial.sortBy);
    const [sortOrder, setSortOrder] = useState<SortOrder>(initial.sortOrder);
    const [page, setPage] = useState(initial.page);
    const limit = 50;
    const refreshCounter = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ filters, kpiMode, sortBy, sortOrder }));
        } catch { /* ignore quota/private-mode */ }
    }, [filters, kpiMode, sortBy, sortOrder]);

    // Filter options follow the company: every other picker offers only what that company has,
    // so a supplier bought only by another company is not a dead option. The guard drops a slow
    // response for a company the user has already switched away from.
    const companyCode = filters.companyCode;
    useEffect(() => {
        let cancelled = false;
        consolidatedBusinessApi.getFilters(companyCode)
            .then(o => { if (!cancelled) setFilterOptions(o); })
            .catch(err => console.error('Failed to load filter options:', err));
        return () => { cancelled = true; };
    }, [companyCode]);

    /**
     * The list and the stats are two independent claims about the same population, so they are
     * settled independently: with Promise.all a failing list ALSO threw away a perfectly good
     * stats response, which is how one broken sort column ("order by list must be unique",
     * SQL error 169) turned into a page with no rows AND six empty tiles. Whatever came back
     * is shown; only the part that failed reports an error.
     *
     * `isCurrent` is the same guard as before — a slow response from a superseded filter state
     * must not overwrite a newer one.
     */
    const load = useCallback((isCurrent: () => boolean) => {
        setLoading(true);
        setError(null);

        const listP = consolidatedBusinessApi.list({ page, limit, sortBy, sortOrder, kpiMode, filters })
            .then(r => { if (isCurrent()) { setItems(r.items); setTotal(r.total); } });
        const statsP = consolidatedBusinessApi.getStats(filters, kpiMode)
            .then(r => { if (isCurrent()) setStats(r); });

        return Promise.allSettled([listP, statsP]).then(results => {
            if (!isCurrent()) return;
            const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
            if (failed.length) {
                const msg = failed[0].reason instanceof Error ? failed[0].reason.message : String(failed[0].reason);
                setError(failed.length === results.length ? msg : `${msg} (part of the page could not be loaded)`);
            }
            setLoading(false);
        });
    }, [page, limit, sortBy, sortOrder, kpiMode, filters]);

    useEffect(() => {
        let cancelled = false;
        const current = ++refreshCounter.current;
        void load(() => !cancelled && current === refreshCounter.current);
        return () => { cancelled = true; };
    }, [load]);

    // Every filter writer goes through here — the stat tiles, the pills, the checkboxes — so the
    // status/hideLost invariant is enforced once instead of in each of them.
    const setFilters = useCallback((f: ConsBusinessFilters) => {
        setFiltersState(reconcileFilters(f));
        setPage(1);
    }, []);

    const setKpiMode = useCallback((m: KpiMode) => {
        setKpiModeState(m);
        setPage(1);
    }, []);

    const setSort = useCallback((field: ConsBusinessSortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'ASC' ? 'DESC' : 'ASC'));
            } else {
                setSortOrder('DESC');
            }
            return field;
        });
        setPage(1);
    }, []);

    // Same load path as the effect — after a write there is no reason for a second, subtly
    // different copy of it (the old one had already drifted: it omitted `limit` from its deps).
    const refresh = useCallback(() => {
        const current = ++refreshCounter.current;
        void load(() => current === refreshCounter.current);
    }, [load]);

    return {
        items, total, loading, error, stats, filterOptions,
        filters, kpiMode, sortBy, sortOrder, page, limit,
        setFilters, setKpiMode, setSort, setPage, refresh,
    };
}
